import { supabase } from '@/utils/supabase';
import type {
  UserSession,
  Communication,
  Document,
  BorrowerConsent,
  LOSMapping,
  CommunicationStatus,
  ConsentType,
  DocumentType
} from '../types/database-enhancements';
import { containsAnyFilter } from './postgrestSearch';

// ============================================
// User Session Management
// ============================================

export const sessionService = {
  async createSession(userId: string, tokenHash: string, expiresAt: Date) {
    const { data, error } = await supabase
      .from('user_sessions')
      .insert({
        user_id: userId,
        token_hash: tokenHash,
        expires_at: expiresAt.toISOString(),
        ip_address: window.location.hostname, // In production, get from request
        user_agent: navigator.userAgent
      })
      .select()
      .single();

    if (error) {
throw error;
}
    return data as UserSession;
  },

  async getUserSessions(userId: string) {
    const { data, error } = await supabase
      .from('user_sessions')
      .select('*')
      .eq('user_id', userId)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) {
throw error;
}
    return data as UserSession[];
  },

  async revokeSession(sessionId: string) {
    const { error } = await supabase
      .from('user_sessions')
      .delete()
      .eq('id', sessionId);

    if (error) {
throw error;
}
  },

  async cleanupExpiredSessions() {
    const { error } = await supabase
      .from('user_sessions')
      .delete()
      .lt('expires_at', new Date().toISOString());

    if (error) {
throw error;
}
  }
};

// ============================================
// Communications Tracking
// ============================================

const COMMUNICATION_SEARCH_COLUMNS = ['subject', 'content', 'email_address', 'phone_number'] as const;

export interface CommunicationPageQuery {
  /** 1-based page number. */
  page: number;
  pageSize: number;
  type?: Communication['type'];
  /** Raw `communications.status` values to include. */
  statuses?: readonly string[];
  /** Literal, case-insensitive text matched against subject, content, email and phone. */
  search?: string;
}

export interface CommunicationPage {
  rows: Communication[];
  total: number;
}

export const communicationService = {
  async logCommunication(communication: Omit<Communication, 'id' | 'created_at' | 'organization_id'>) {
    const { data: profile } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', communication.user_id)
      .single();

    if (!profile) {
throw new Error('User profile not found');
}

    const { data, error } = await supabase
      .from('communications')
      .insert({
        ...communication,
        organization_id: profile.organization_id
      })
      .select()
      .single();

    if (error) {
throw error;
}
    return data as Communication;
  },

  async updateCommunicationStatus(
    communicationId: string, 
    status: CommunicationStatus,
    timestamp?: Date
  ) {
    const updateData: Record<string, unknown> = { status };
    
    // Add appropriate timestamp based on status
    if (status === 'sent') {
      updateData.delivered_at = (timestamp || new Date()).toISOString();
    }
    if (status === 'delivered') {
updateData.delivered_at = (timestamp || new Date()).toISOString();
}
    if (status === 'opened') {
updateData.opened_at = (timestamp || new Date()).toISOString();
}
    if (status === 'clicked') {
updateData.clicked_at = (timestamp || new Date()).toISOString();
}

    const { data, error } = await supabase
      .from('communications')
      .update(updateData)
      .eq('id', communicationId)
      .select()
      .single();

    if (error) {
throw error;
}
    return data as Communication;
  },

  /** One page of the company message log, newest first, with the exact matching total. */
  async getCommunicationPage(query: CommunicationPageQuery): Promise<CommunicationPage> {
    const { page, pageSize, type, statuses, search } = query;
    let request = supabase.from('communications').select('*', { count: 'exact' });

    if (type) {
request = request.eq('type', type);
}
    if (statuses) {
request = request.in('status', [...statuses]);
}
    const searchFilter = search === undefined ? null : containsAnyFilter(COMMUNICATION_SEARCH_COLUMNS, search);
    if (searchFilter) {
request = request.or(searchFilter);
}

    const from = (page - 1) * pageSize;
    const { data, error, count } = await request
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .range(from, from + pageSize - 1);

    if (error) {
throw error;
}
    return { rows: (data ?? []) as Communication[], total: count ?? 0 };
  },

  /** Total messages visible to the caller, ignoring filters. */
  async countCommunications(): Promise<number> {
    const { count, error } = await supabase
      .from('communications')
      .select('id', { count: 'exact', head: true });

    if (error) {
throw error;
}
    return count ?? 0;
  }
};

// ============================================
// Document Management
// ============================================

export const documentService = {
  async uploadDocument(
    file: File,
    documentType: DocumentType,
    entityType?: Document['entity_type'],
    entityId?: string,
    tags?: string[]
  ) {
    // Get user's organization
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
throw new Error('Not authenticated');
}

    const { data: profile } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (!profile) {
throw new Error('User profile not found');
}

    // Upload file to storage
    const fileExt = file.name.split('.').pop();
    const fileName = `${profile.organization_id}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
    
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('documents')
      .upload(fileName, file);

    if (uploadError) {
throw uploadError;
}

    // Create document record
    const { data, error } = await supabase
      .from('documents')
      .insert({
        organization_id: profile.organization_id,
        uploaded_by_user_id: user.id,
        filename: file.name,
        document_type: documentType,
        mime_type: file.type,
        size_bytes: file.size,
        storage_path: uploadData.path,
        cdn_url: supabase.storage.from('documents').getPublicUrl(uploadData.path).data.publicUrl,
        entity_type: entityType,
        entity_id: entityId,
        tags: tags || []
      })
      .select()
      .single();

    if (error) {
throw error;
}
    return data as Document;
  },

  async getDocuments(filters: {
    entity_type?: Document['entity_type'];
    entity_id?: string;
    document_type?: string;
    tags?: string[];
  }) {
    let query = supabase.from('documents').select('*');

    if (filters.entity_type) {
query = query.eq('entity_type', filters.entity_type);
}
    if (filters.entity_id) {
query = query.eq('entity_id', filters.entity_id);
}
    if (filters.document_type) {
query = query.eq('document_type', filters.document_type);
}
    if (filters.tags && filters.tags.length > 0) {
      query = query.contains('tags', filters.tags);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
throw error;
}
    return data as Document[];
  },

  async deleteDocument(documentId: string) {
    // Get document details first
    const { data: doc, error: fetchError } = await supabase
      .from('documents')
      .select('storage_path')
      .eq('id', documentId)
      .single();

    if (fetchError) {
throw fetchError;
}

    // Delete from storage
    if (doc?.storage_path) {
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([doc.storage_path]);

      if (storageError) {
console.error('Storage deletion error:', storageError);
}
    }

    // Delete record
    const { error } = await supabase
      .from('documents')
      .delete()
      .eq('id', documentId);

    if (error) {
throw error;
}
  }
};

// ============================================
// Consent Management
// ============================================

export const consentService = {
  async recordConsent(
    borrowerId: string,
    consentType: ConsentType,
    granted: boolean,
    method: BorrowerConsent['method'],
    formVersion?: string
  ) {
    // Get organization from borrower
    const { data: borrower } = await supabase
      .from('borrowers')
      .select('organization_id')
      .eq('id', borrowerId)
      .single();

    if (!borrower) {
throw new Error('Borrower not found');
}

    const { data, error } = await supabase
      .from('borrower_consents')
      .upsert({
        borrower_id: borrowerId,
        organization_id: borrower.organization_id,
        consent_type: consentType,
        granted,
        granted_at: granted ? new Date().toISOString() : null,
        granted_by: 'borrower',
        method,
        form_version: formVersion,
        ip_address: window.location.hostname // In production, get from request
      })
      .select()
      .single();

    if (error) {
throw error;
}
    return data as BorrowerConsent;
  },

  async getConsents(borrowerId: string) {
    const { data, error } = await supabase
      .from('borrower_consents')
      .select('*')
      .eq('borrower_id', borrowerId)
      .order('created_at', { ascending: false });

    if (error) {
throw error;
}
    return data as BorrowerConsent[];
  },

  async revokeConsent(borrowerId: string, consentType: ConsentType) {
    const { data, error } = await supabase
      .from('borrower_consents')
      .update({
        granted: false,
        revoked_at: new Date().toISOString()
      })
      .eq('borrower_id', borrowerId)
      .eq('consent_type', consentType)
      .select()
      .single();

    if (error) {
throw error;
}
    return data as BorrowerConsent;
  }
};

// ============================================
// LOS Integration
// ============================================

export const losService = {
  async createMapping(entityType: LOSMapping['entity_type'], ourId: string, losId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
throw new Error('Not authenticated');
}

    const { data: profile } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (!profile) {
throw new Error('User profile not found');
}

    const { data, error } = await supabase
      .from('los_mappings')
      .insert({
        organization_id: profile.organization_id,
        entity_type: entityType,
        our_id: ourId,
        los_id: losId
      })
      .select()
      .single();

    if (error) {
throw error;
}
    return data as LOSMapping;
  },

  async getMapping(entityType: LOSMapping['entity_type'], ourId: string) {
    const { data, error } = await supabase
      .from('los_mappings')
      .select('*')
      .eq('entity_type', entityType)
      .eq('our_id', ourId)
      .single();

    if (error && error.code !== 'PGRST116') {
throw error;
} // Ignore not found errors
    return data as LOSMapping | null;
  }
};
